import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { DomainsVdpConfigDtoOutput, ServicesProvider } from '@ekhodealer/ekho-common';
import type { UseMutationResult } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to update VDP configuration for a services provider
 */
export const updateDomainsVdpConfig = async (
	servicesProviderId: string,
	data: DomainsVdpConfigDtoOutput,
): Promise<ServicesProvider> => {
	const response = await apiClient.patch<ServicesProvider>(
		configurationEndpoints.domainsVdpConfig(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for updating VDP configuration for a services provider
 */
export const useUpdateDomainsVdpConfig = (): UseMutationResult<
	ServicesProvider,
	Error,
	DomainsVdpConfigDtoOutput
> => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: DomainsVdpConfigDtoOutput): Promise<ServicesProvider> => {
			if (!servicesProviderId) {
				throw new Error('Unable to update VDP config without a services provider ID.');
			}
			return updateDomainsVdpConfig(servicesProviderId, data);
		},
		onSuccess: (data) => {
			queryClient.setQueryData(queries.servicesProviders.detail(data.id).queryKey, data);
		},
	});
};
